import { useCallback, useState } from 'react'

import { printThermalReceipt } from '@/components/pos/posReceipt'
import { printTaxInvoice } from '@/components/pos/posTaxInvoiceUtils'
import { useSaleDocumentPrintRefs } from '@/components/sales/SaleDocumentPrintHosts'
import { fetchSaleReceipt, fetchSaleTaxInvoice } from '@/lib/api/sales'

/**
 * Reprint thermal receipt or A4 tax invoice for a completed sale.
 */
export function useSaleDocumentPrint() {
  const printRefs = useSaleDocumentPrintRefs()
  const [busySaleId, setBusySaleId] = useState(null)
  const [busyAction, setBusyAction] = useState('')
  const [error, setError] = useState('')

  const reprintReceipt = useCallback(async (saleId) => {
    setBusySaleId(saleId)
    setBusyAction('receipt')
    setError('')
    try {
      const document = await fetchSaleReceipt(saleId)
      printRefs.setReceipt(document)
      await new Promise((resolve) => window.setTimeout(resolve, 100))
      printThermalReceipt(printRefs.receiptRootRef.current)
    } catch {
      setError('Unable to print receipt for this visit.')
    } finally {
      setBusySaleId(null)
      setBusyAction('')
    }
  }, [printRefs])

  const printTaxInvoiceForSale = useCallback(async (saleId) => {
    setBusySaleId(saleId)
    setBusyAction('invoice')
    setError('')
    try {
      const document = await fetchSaleTaxInvoice(saleId)
      printRefs.setTaxInvoice(document)
      await new Promise((resolve) => window.setTimeout(resolve, 100))
      printTaxInvoice(printRefs.taxInvoiceRootRef.current)
    } catch {
      setError('Unable to print tax invoice for this visit.')
    } finally {
      setBusySaleId(null)
      setBusyAction('')
    }
  }, [printRefs])

  return {
    ...printRefs,
    reprintReceipt,
    printTaxInvoiceForSale,
    busySaleId,
    busyAction,
    error,
    setError,
  }
}
